/**
 * Claves y opciones de React Query en un solo lugar. Todo vive en memoria: al cerrar
 * sesión se hace queryClient.clear() y nada queda en el navegador.
 *
 * Varios GET escriben auditoría en el servidor (ver aviso, JSON crudo…): por eso no
 * se refresca al volver a la pestaña y los detalles se consideran frescos varios
 * minutos — cada vista registrada debe corresponder a una acción del analista.
 */

import { QueryClient, queryOptions } from "@tanstack/react-query";
import type { ParsedQuery, SearchFilters, SearchResponse } from "../shared/contracts.js";
import {
  api, isApiError, type AuditParams, type InstitutionListParams, type UsageParams, type UserListParams,
} from "./api.js";
import type { Purpose, SubjectRole } from "./lib/search-context.js";

export const qk = {
  me: ["auth", "me"] as const,
  sessions: ["auth", "sessions"] as const,
  facets: ["meta", "facets"] as const,
  searchView: (id: string) => ["searches", id, "view"] as const,
  searchResults: (id: string) => ["searches", id, "results"] as const,
  history: (scope: "mine" | "institution") => ["searches", "history", scope] as const,
  listing: (id: string) => ["listings", id] as const,
  snapshotRaw: (id: string) => ["snapshots", id, "raw"] as const,
  admin: {
    all: ["admin"] as const,
    institutions: (p: InstitutionListParams) => ["admin", "institutions", "list", p] as const,
    allInstitutions: ["admin", "institutions", "all"] as const,
    institution: (id: string) => ["admin", "institutions", "detail", id] as const,
    users: (p: UserListParams) => ["admin", "users", "list", p] as const,
    institutionUsers: (id: string) => ["admin", "users", "by-institution", id] as const,
    audit: (p: AuditParams) => ["admin", "audit", "list", p] as const,
    recentAudit: (institutionId: string) => ["admin", "audit", "recent", institutionId] as const,
    usage: (p: UsageParams) => ["admin", "usage", p] as const,
    usageMonthly: (month: string) => ["admin", "usage", "monthly", month] as const,
    system: ["admin", "system"] as const,
    targets: ["admin", "targets"] as const,
    agents: ["admin", "agents"] as const,
    socialAccounts: ["admin", "social-accounts"] as const,
  },
};

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        // 4xx no se arreglan reintentando; red caída o 5xx sí, hasta 2 veces.
        retry: (count, err) => count < 2 && !(isApiError(err) && err.status >= 400 && err.status < 500),
      },
      mutations: { retry: false },
    },
  });
}

export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: ({ signal }) => api.auth.me(signal),
  staleTime: 5 * 60_000,
  retry: false,
});

export const facetsQuery = queryOptions({
  queryKey: qk.facets,
  queryFn: ({ signal }) => api.meta.facets(signal),
  staleTime: 5 * 60_000,
});

/**
 * Vista de una búsqueda: lo que la pantalla necesita para pintar el encabezado y el
 * panel en vivo. Recién creada viene del POST (con broad/tookMs/manualLinks); al
 * reabrirla desde el historial se arma desde GET /api/searches/:id.
 */
export interface SearchView {
  id: string;
  queryText: string;
  parsed: ParsedQuery;
  filters: SearchFilters;
  context: {
    purpose: Purpose;
    purposeDetail: string | null;
    caseRef: string | null;
    subjectRole: SubjectRole | null;
  };
  createdAt: string | null;
  user: { fullName: string; email: string } | null;
  broad: boolean | null;
  totalEstimate: number | null;
  tookMs: number | null;
  live: SearchResponse["live"];
  manualLinks: SearchResponse["manualLinks"];
  /** true si se reabrió desde el historial (el panel en vivo sondea en vez de abrir SSE). */
  reopened: boolean;
  /** Hecha con el formulario avanzado (undefined al reabrir: se deduce del ParsedQuery). */
  advanced?: boolean;
  relaxed?: SearchResponse["relaxed"];
}

export function searchViewQuery(id: string) {
  return queryOptions({
    queryKey: qk.searchView(id),
    staleTime: Infinity,
    gcTime: 60 * 60_000,
    queryFn: async ({ signal }): Promise<SearchView> => {
      const r = await api.searches.get(id, signal);
      const enc = encodeURIComponent(id);
      return {
        id: r.id,
        queryText: r.queryText,
        parsed: r.parsed,
        filters: r.filters,
        context: { purpose: r.purpose, purposeDetail: r.purposeDetail, caseRef: r.caseRef, subjectRole: r.subjectRole },
        createdAt: r.createdAt,
        user: { fullName: r.user.fullName, email: r.user.email },
        broad: null,
        totalEstimate: r.resultCount,
        tookMs: null,
        live: r.liveRequested
          ? { status: "running", streamUrl: `/api/searches/${enc}/live/stream`, pollUrl: `/api/searches/${enc}/live` }
          : { status: "disabled", streamUrl: null, pollUrl: null },
        manualLinks: r.manualLinks ?? [],
        reopened: true,
      };
    },
  });
}

export function listingQuery(id: string) {
  return queryOptions({
    queryKey: qk.listing(id),
    queryFn: ({ signal }) => api.listings.get(id, signal),
    staleTime: 10 * 60_000,
  });
}

export function snapshotRawQuery(id: string) {
  return queryOptions({
    queryKey: qk.snapshotRaw(id),
    queryFn: ({ signal }) => api.snapshots.raw(id, signal),
    staleTime: Infinity,
  });
}

/** Todas las instituciones (para selects de filtros); recorre el cursor con un tope. */
export const allInstitutionsQuery = queryOptions({
  queryKey: qk.admin.allInstitutions,
  staleTime: 60_000,
  queryFn: async ({ signal }) => {
    const items = [];
    let cursor: string | null = null;
    for (let i = 0; i < 20; i++) {
      const page = await api.admin.institutions({ cursor }, signal);
      items.push(...page.items);
      cursor = page.nextCursor;
      if (!cursor) break;
    }
    return items.sort((a, b) => a.name.localeCompare(b.name, "es"));
  },
});

export const systemQuery = queryOptions({
  queryKey: qk.admin.system,
  queryFn: ({ signal }) => api.admin.system(signal),
  staleTime: 30_000,
});

/** PCs recolectores: se refresca seguido para ver "en línea" y lo que está corriendo. */
export const agentsQuery = queryOptions({
  queryKey: qk.admin.agents,
  queryFn: ({ signal }) => api.admin.agents(signal),
  staleTime: 10_000,
  refetchInterval: 15_000,
});

/** Cuentas de redes sociales del scraper (estado reportado por el PC). */
export const socialAccountsQuery = queryOptions({
  queryKey: qk.admin.socialAccounts,
  queryFn: ({ signal }) => api.admin.socialAccounts(signal),
  staleTime: 10_000,
  refetchInterval: 15_000,
});

/** Fuentes sociales (targets del scraper): todas, agrupadas en la página por categoría. */
export const targetsQuery = queryOptions({
  queryKey: qk.admin.targets,
  queryFn: ({ signal }) => api.admin.targets({}, signal),
  staleTime: 30_000,
});
